/** El tablón inicial del guion más una pieza por cada acuerdo del minijuego. */
export const CANTIDAD_DE_ACUERDOS_DEL_PUENTE = 3;
export const CANTIDAD_DE_PLANCHAS_DEL_PUENTE = CANTIDAD_DE_ACUERDOS_DEL_PUENTE + 1;

/** Progreso local monotónico: cambiar frameloop puede reiniciar el Clock de R3F. */
export function avanzarLlegadaDePlancha(progreso: number, delta: number, menosMovimiento: boolean): number {
  if (menosMovimiento) return 1;
  const actual = Number.isFinite(progreso) ? Math.min(1, Math.max(0, progreso)) : 0;
  const paso = Number.isFinite(delta) && delta > 0 ? Math.min(delta, 0.05) : 0;
  return Math.min(1, actual + paso / 0.62);
}

export interface EventoDeReparacionPuente {
  readonly plankFliesTo?: string | undefined;
  readonly restore?: readonly string[] | undefined;
}

export interface EstadoVisualDeReparacionPuente {
  readonly environment: Readonly<Record<string, string>>;
  readonly planchasVisibles: number;
  readonly restaurado: boolean;
}

export interface PoseDeLlegadaDePlancha {
  readonly desplazamientoX: number;
  readonly desplazamientoY: number;
  readonly rotacionZ: number;
  readonly escala: number;
  /** Intensidad 0–1 del pulso de contacto; se omite con menos movimiento. */
  readonly pulso: number;
  readonly terminada: boolean;
}

/**
 * Una sola curva para el vuelo y el asentamiento visual. El arco es corto,
 * la llegada no rebota de forma llamativa y el pulso sirve como contacto
 * suave, no como premio. `progreso` va de 0 a 1.
 */
export function poseDeLlegadaDePlancha(
  progreso: number,
  menosMovimiento = false,
): PoseDeLlegadaDePlancha {
  const t = Math.min(1, Math.max(0, Number.isFinite(progreso) ? progreso : 0));
  if (menosMovimiento || t >= 1) {
    return {
      desplazamientoX: 0,
      desplazamientoY: 0,
      rotacionZ: 0,
      escala: 1,
      pulso: 0,
      terminada: true,
    };
  }

  const finDelVuelo = 0.78;
  const vuelo = Math.min(1, t / finDelVuelo);
  const ease = 1 - (1 - vuelo) ** 3;
  const asentamiento = Math.max(0, (t - finDelVuelo) / (1 - finDelVuelo));
  const pulso = asentamiento > 0 && asentamiento < 1
    ? Math.sin(Math.PI * asentamiento)
    : 0;
  const asentamientoSuave =
    0.016 * Math.sin(2 * Math.PI * asentamiento) * Math.exp(-3 * asentamiento);

  return {
    desplazamientoX: 0.58 * (1 - ease),
    desplazamientoY: 0.34 * (1 - ease) + 0.4 * Math.sin(Math.PI * vuelo) + asentamientoSuave,
    rotacionZ: 0.1 * (1 - ease),
    escala: 0.97 + 0.03 * ease,
    pulso,
    terminada: t >= 1,
  };
}

/**
 * Convierte los eventos ya declarados por EP01 y el avance efímero de sus
 * tres tarjetas en una vista coherente del puente. No guarda elecciones ni
 * escribe progreso: el contador vive solo mientras dura el episodio.
 */
export function estadoVisualDeReparacionPuente({
  sceneId,
  environment,
  acuerdosColocados,
  event,
  restauracionConfirmada,
}: {
  readonly sceneId: string;
  readonly environment: Readonly<Record<string, string>>;
  readonly acuerdosColocados: number;
  readonly event?: EventoDeReparacionPuente | undefined;
  readonly restauracionConfirmada: boolean;
}): EstadoVisualDeReparacionPuente {
  const esEscenaDeReconstruccion = sceneId === "s07_reconstruccion";
  const acuerdos = esEscenaDeReconstruccion
    ? Math.max(0, Math.min(CANTIDAD_DE_ACUERDOS_DEL_PUENTE, Math.floor(acuerdosColocados)))
    : 0;
  const eventoLlegaTablon = event?.plankFliesTo === "bridge_main";
  const inicioParcial = environment.bridge_main === "partially_fixed" || eventoLlegaTablon;
  const restauracionSolicitada = event?.restore?.includes("bridge_main") ?? false;
  const restaurado =
    environment.bridge_main === "fixed" ||
    restauracionConfirmada ||
    (esEscenaDeReconstruccion && restauracionSolicitada && acuerdos === CANTIDAD_DE_ACUERDOS_DEL_PUENTE);
  const planchasVisibles = restaurado
    ? CANTIDAD_DE_PLANCHAS_DEL_PUENTE
    : inicioParcial
      ? Math.min(CANTIDAD_DE_PLANCHAS_DEL_PUENTE, 1 + acuerdos)
      : 0;

  return {
    environment: restaurado ? { ...environment, bridge_main: "fixed" } : environment,
    planchasVisibles,
    restaurado,
  };
}

/** Centros de planchas uniformes, con juntas pequeñas y márgenes en las orillas. */
export function offsetsDePlanchasDeReparacion(
  longitudDelVano: number,
  cantidad = CANTIDAD_DE_PLANCHAS_DEL_PUENTE,
  junta = 0.08,
): readonly number[] {
  const largoDePlancha = largoDePlanchasDeReparacion(longitudDelVano, cantidad, junta);
  const juntaSegura = Math.min(Math.max(0, junta), longitudDelVano / (cantidad * 3));
  return Array.from(
    { length: cantidad },
    (_, indice) => -longitudDelVano / 2 + largoDePlancha / 2 + indice * (largoDePlancha + juntaSegura),
  );
}

export function largoDePlanchasDeReparacion(
  longitudDelVano: number,
  cantidad = CANTIDAD_DE_PLANCHAS_DEL_PUENTE,
  junta = 0.08,
): number {
  if (!Number.isFinite(longitudDelVano) || longitudDelVano <= 0) {
    throw new Error("El vano del puente debe ser una longitud positiva y finita.");
  }
  if (!Number.isInteger(cantidad) || cantidad < 1) {
    throw new Error("La cantidad de planchas debe ser un entero positivo.");
  }

  const juntaSegura = Math.min(Math.max(0, junta), longitudDelVano / (cantidad * 3));
  return (longitudDelVano - juntaSegura * (cantidad - 1)) / cantidad;
}
